// Session AHJ: correct the reference on an issued Call-Out Attendance Report.
//
// The reference used to be written once, inside the generation route, and never again. A wrong one
// (the internal CO- id on a report whose invoice was raised in Xero by hand, or a number typed
// wrongly) could only be fixed by deleting the call-out and recreating it, which throws away the
// record, its charge and its Drive filing in order to fix a string.
//
// THE HONEST PART, and the reason this modal says more than it asks: correcting the reference does
// NOT change the PDF that was already rendered and filed. The reference is baked into those bytes
// and into the Drive filename at generation time. Without saying so, the office would correct the
// reference, believe the customer's document now agrees, and it would not.
import { useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { CalloutListItem } from "../../lib/api";
import { Button } from "../../components/Button";
import { MODAL_ACTIONS, MODAL_ACTION_BTN, ModalShell } from "../../components/ModalShell";

export function CalloutReportRefModal({
  callout,
  onClose,
  onSaved,
}: {
  callout: CalloutListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const current = callout.reportRef ?? "";
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = value.trim();
  const canSave = trimmed.length > 0 && trimmed !== current && !saving;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.callouts.updateReportRef(callout.id, trimmed);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "The reference could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell ariaLabel="Correct the report reference" onClose={() => !saving && onClose()} width="max-w-lg" panelClassName="p-6">
      <h2 className="mb-1 font-heading text-lg text-ele-strong">Correct the report reference</h2>
      <p className="mb-4 text-sm text-ele-body">
        The reference shown on the call-out attendance report for {callout.ref}.
      </p>

      <label htmlFor="callout-report-ref" className="mb-1 block text-xs font-menu uppercase tracking-wide text-ele-body">
        Reference
      </label>
      <input
        id="callout-report-ref"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="INV-2431"
        className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
      />
      <p className="mt-1 text-xs text-ele-body opacity-70">Currently {current}.</p>

      {/* The locked functional amber (advisory), on its own tinted panel with a full border: never a
          left stripe (the platform-wide no-left-stripe rule). */}
      <p className="mt-4 rounded-ele border border-ele-amber-line bg-ele-amber-wash p-3 text-xs text-ele-amber-wash-ink">
        This corrects the platform record only. The PDF already generated and filed keeps the
        reference it was issued under. To produce a document that shows the corrected reference,
        generate the report again.
      </p>

      {error && (
        <p role="alert" className="mt-3 text-sm text-ele-danger">
          {error}
        </p>
      )}

      <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
        >
          Cancel
        </button>
        <Button
          onClick={() => void save()}
          disabled={!canSave}
          className={MODAL_ACTION_BTN}
        >
          {saving ? "Saving..." : "Save reference"}
        </Button>
      </div>
    </ModalShell>
  );
}
